import React, { useCallback, useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'
import Slider from 'react-slick'
import Slide from './slide'

const DEFAULT_DELAY_MS = 8000

const Slideshow = ({ id = 'homepage' }) => {
  const { data: slideshow, isLoading } = useSWR(`/slideshows/${id}`, fetcher)
  const sliderRef = useRef(null)
  const [current, setCurrent] = useState(0)
  const [videoPlaying, setVideoPlaying] = useState(false)

  const slides = slideshow?.slides || []
  const hasSlides = slides.length > 0
  const slideCount = slides.length
  const delay = slideshow?.delay_ms > 0 ? slideshow.delay_ms : DEFAULT_DELAY_MS

  // Advance after `delay` unless the current slide's video is playing. The
  // timer restarts whenever the slide changes (timer, arrows, dots, swipe)
  // and when a playing video pauses or errors.
  useEffect(() => {
    if (slideCount <= 1 || videoPlaying) return
    const timer = setTimeout(() => sliderRef.current?.slickNext(), delay)
    return () => clearTimeout(timer)
  }, [current, slideCount, delay, videoPlaying])

  const handleVideoPlay = useCallback(() => setVideoPlaying(true), [])
  const handleVideoPause = useCallback(() => setVideoPlaying(false), [])
  const handleVideoEnded = useCallback(() => {
    setVideoPlaying(false)
    if (slideCount > 1) sliderRef.current?.slickNext()
  }, [slideCount])

  const CustomPrevArrow = () => (
    <button
      className='absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-1.5 sm:p-2 rounded-full transition-all duration-200 backdrop-blur-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100'
      onClick={() => sliderRef.current?.slickPrev()}
      aria-label="Previous slide"
    >
      <svg className='w-5 h-5 sm:w-6 sm:h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
      </svg>
    </button>
  )

  const CustomNextArrow = () => (
    <button
      className='absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-1.5 sm:p-2 rounded-full transition-all duration-200 backdrop-blur-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100'
      onClick={() => sliderRef.current?.slickNext()}
      aria-label="Next slide"
    >
      <svg className='w-5 h-5 sm:w-6 sm:h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
      </svg>
    </button>
  )

  const PlaceholderSlide = () => (
    <div className='w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900'>
      <div className='text-center text-gray-400 dark:text-gray-600'>
        <svg className='w-16 h-16 mx-auto mb-2 opacity-50' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1} d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' />
        </svg>
      </div>
    </div>
  )

  var settings = {
    customPaging: _slide => {
      return (
        <div className='w-2 h-2 rounded-full bg-white opacity-50 hover:opacity-100' />
      )
    },
    dots: true,
    dotsClass:
      'absolute top-2 w-full !flex flex-row justify-center align-center space-x-3',
    infinite: true,
    // Changing slide unmounts the outgoing slide's player (stopping it)
    beforeChange: (_oldIndex, newIndex) => {
      setVideoPlaying(false)
      setCurrent(newIndex)
    },
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false // Disable default arrows since we're using custom ones
  }

  // Show placeholder while loading or when no slides
  if (isLoading) {
    return (
      <div className='sm:aspect-[16/9] rounded-lg w-full h-full shadow-lg overflow-hidden relative bg-white dark:bg-gray-900'>
        <PlaceholderSlide />
      </div>
    )
  }

  if (!hasSlides) {
    return (
      <div className='sm:aspect-[16/9] rounded-lg w-full h-full shadow-lg overflow-hidden relative bg-white dark:bg-gray-900'>
        <PlaceholderSlide />
      </div>
    )
  }

  return (
    <div className='sm:aspect-[16/9] rounded-lg w-full h-full shadow-lg overflow-hidden relative bg-white dark:bg-gray-900 group'>
      {slides.length > 1 && (
        <>
          <CustomPrevArrow />
          <CustomNextArrow />
        </>
      )}
      <Slider ref={sliderRef} {...settings}>
        {slides.map((slide, index) => (
          <Slide
            key={slide.key}
            slide={slide}
            active={index === current}
            onVideoPlay={handleVideoPlay}
            onVideoPause={handleVideoPause}
            onVideoEnded={handleVideoEnded}
          />
        ))}
      </Slider>
    </div>
  )
}

export default Slideshow
